/* eslint-disable jsx-a11y/no-redundant-roles -- Preserve Safari list semantics when list-style is none. */
import React from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiArrowUpRight, FiArrowDown } from "react-icons/fi";
import solutionHeroImage from "../../Assets/blogs/klean-leaf-laundry-sheet-production-lines.webp";
import brandBox from "../../Assets/blogs/your-brand-laundry-detergent-sheets-box.webp";
import refillJars from "../../Assets/solutions/detergent-sheets-jar.webp";
import packagingSamples from "../../Assets/blogs/laundry-detergent-sheets-package-format-dieline-options.webp";
import { LAUNDRY_COLLECTION_PATH, PACKAGING_PATH, MOQ_PATH, processSteps, customizationOptions,
  businessFacts, audiences, bulkBenefits, manufacturingSupport, packagingOptions, broaderProducts } from "./solutionContent";

export function SectionHeading({ id, eyebrow, title, children }) {
  return <div className="solution-heading">
    {eyebrow && <p className="solution-eyebrow">{eyebrow}</p>}
    <h2 id={id}>{title}</h2>{children && <p>{children}</p>}
  </div>;
}
export function IconFeatureGrid({ items, className = "" }) {
  return <ul role="list" className={`solution-feature-grid ${className}`.trim()}>
    {items.map(({ icon: Icon, title, copy }) => <li key={title}>
      <Icon aria-hidden="true" focusable="false" />
      <div><h3>{title}</h3>{copy && <p>{copy}</p>}</div>
    </li>)}
  </ul>;
}
export function ProcessTimeline({ steps }) {
  return <ol role="list" className="solution-timeline">
    {steps.map(({ icon: Icon, title, copy }, index) => <li key={title}>
      <div className="solution-step-marker"><span>{String(index + 1).padStart(2, "0")}</span><Icon aria-hidden="true" focusable="false" /></div>
      <div className="solution-step-copy"><h3>{title}</h3><p>{copy}</p></div>
    </li>)}
  </ol>;
}
export function ProductCategoryCards({ items }) {
  return <ul role="list" className="solution-product-grid">
    {items.map(({ product, title, to }) => <li key={title}>
      <Link to={to} className="solution-product">
        <div className="solution-product-image"><img src={product.images[0].src} alt={product.images[0].alt} loading="lazy" decoding="async" width="320" height="240" /></div>
        <span>{title}<FiArrowUpRight aria-hidden="true" /></span>
      </Link>
    </li>)}
  </ul>;
}
export function SolutionHero({ onContact }) {
  return <section className="solution-hero" aria-labelledby="solution-title">
    <div className="solution-container">
      <div className="solution-hero-grid">
        <div className="solution-hero-copy">
          <p className="solution-eyebrow"><span className="solution-status-dot" />Private Label Solutions</p>
          <h1 id="solution-title">Private Label <br />Laundry Detergent <br /><span>Sheet Solutions</span></h1>
          <p className="solution-lead">From formula and fragrance to packaging and delivery, we manufacture retail-ready laundry sheets for your brand.</p>
          <div className="solution-actions">
            <Link className="solution-button" to={LAUNDRY_COLLECTION_PATH}>Explore Laundry Sheets <FiArrowRight aria-hidden="true" /></Link>
            <button className="solution-text-link" onClick={onContact} type="button">Start Your Project <FiArrowUpRight aria-hidden="true" /></button>
          </div>
        </div>
        <figure className="solution-hero-visual">
          <div className="solution-hero-image">
            <img src={solutionHeroImage} alt="Klean Leaf production lines with large rolls of laundry sheet material and a central factory aisle" width="1280" height="960" decoding="async" />
          </div>
          <figcaption><span>Laundry sheet production at Klean Leaf.</span><span>Built for your brand.</span></figcaption>
        </figure>
      </div>
      <div className="solution-hero-bottom"><span>Laundry detergent sheets manufacturer</span><Link to="#choose-your-solution" state={{ scrollToTop: true }}>Find your solution <FiArrowDown aria-hidden="true" /></Link></div>
    </div>
  </section>;
}
export function SolutionPaths() {
  return <section id="choose-your-solution" className="solution-section solution-paths" aria-labelledby="solution-paths-title">
    <div className="solution-container">
      <SectionHeading id="solution-paths-title" title="Choose Your Solution" />
      <div className="solution-path-grid">
        <article className="solution-path solution-path-primary">
          <div className="solution-path-copy">
            <p className="solution-eyebrow">Private Label</p><h3>Build Your Own <br />Laundry Sheet Brand</h3>
            <p>Custom formula, fragrance, packaging and branding for retail-ready products.</p>
            <Link className="solution-text-link" to="#private-label-process" state={{ scrollToTop: true }}>See Private Label Process <FiArrowRight aria-hidden="true" /></Link>
          </div>
          <img src={brandBox} alt="Laundry detergent sheet box with Your Brand artwork" width="1433" height="1075" loading="lazy" decoding="async" />
        </article>
        <article className="solution-path solution-path-secondary">
          <div className="solution-path-copy">
            <p className="solution-eyebrow">Bulk &amp; Refill</p><h3>Bulk Laundry Sheets <br />for Refill Retail</h3>
            <p>Loose sheets for refill stores, zero-waste retailers and local packaging.</p>
            <Link className="solution-text-link" to="#bulk-refill" state={{ scrollToTop: true }}>Explore Bulk Supply <FiArrowRight aria-hidden="true" /></Link>
          </div>
          <img src={refillJars} alt="Laundry detergent sheets in branded refill jars beside kraft envelopes" width="500" height="500" loading="lazy" decoding="async" />
        </article>
      </div>
    </div>
  </section>;
}
export function PrivateLabelProcess() {
  return <section id="private-label-process" className="solution-section solution-process" aria-labelledby="solution-process-title">
    <div className="solution-container">
      <SectionHeading id="solution-process-title" eyebrow="Your Private Label Journey" title="From Sample to Shipment">A clear path from product selection to finished private label inventory.</SectionHeading>
      <ProcessTimeline steps={processSteps} />
    </div>
  </section>;
}
export function BrandCustomization() {
  return <section className="solution-section solution-customization" aria-labelledby="solution-customization-title">
    <div className="solution-container solution-customization-layout">
      <div>
        <SectionHeading id="solution-customization-title" eyebrow="Make It Yours" title="Built Around Your Brand">Shape your laundry sheets around your customers, market and brand.</SectionHeading>
        <Link className="solution-text-link" to={LAUNDRY_COLLECTION_PATH}>Explore Laundry Sheet Options <FiArrowUpRight aria-hidden="true" /></Link>
      </div>
      <IconFeatureGrid items={customizationOptions} className="solution-customization-grid" />
    </div>
  </section>;
}
export function PackagingShowcase() {
  return <section className="solution-section solution-packaging" aria-labelledby="solution-packaging-title">
    <div className="solution-container">
      <div className="solution-section-topline">
        <SectionHeading id="solution-packaging-title" eyebrow="From Sheet to Shelf" title="Packaging Designed for Your Market">Choose packaging based on product format, positioning and order quantity.</SectionHeading>
        <Link className="solution-text-link" to={PACKAGING_PATH}>See Product &amp; Packaging Details <FiArrowUpRight aria-hidden="true" /></Link>
      </div>
      <div className="solution-packaging-layout">
        <figure className="solution-packaging-photo">
          <img src={packagingSamples} alt="Klean Leaf laundry sheet packaging samples, folded boxes, envelopes and artwork dielines on a worktable" width="1200" height="840" loading="lazy" decoding="async" />
          <figcaption>Packaging samples &amp; artwork development</figcaption>
        </figure>
        <ul role="list" className="solution-packaging-grid">
          {packagingOptions.map(({ id, name, items }) => <li key={id}>
            <img src={items[0].image} alt={`${name} packaging format`} width={items[0].width} height={items[0].height} loading="lazy" decoding="async" />
            <h3>{name}</h3>
          </li>)}
        </ul>
      </div>
    </div>
  </section>;
}
export function StatsStrip({ items }) {
  return <dl className="solution-stats-strip">
    {items.map(({ value, label }) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
  </dl>;
}
export function BusinessFacts() {
  return <section className="solution-business" aria-label="Key business information">
    <div className="solution-container">
      <StatsStrip items={businessFacts} />
      <p className="solution-fine-print">*Starting retail package units per SKU. MOQ varies by product, packaging and formulation. <Link to={MOQ_PATH}>View order requirements <FiArrowUpRight aria-hidden="true" /></Link></p>
    </div>
  </section>;
}
export function WhoWeSupport() {
  return <section className="solution-section solution-audiences" aria-labelledby="solution-audiences-title">
    <div className="solution-container solution-audience-layout">
      <SectionHeading id="solution-audiences-title" title="Built for Growing Brands" />
      <IconFeatureGrid items={audiences} className="solution-audience-grid" />
    </div>
  </section>;
}
export function ImageTextSplit({ image, alt, width, height, children }) {
  return <div className="solution-image-text-split">
    <figure><img src={image} alt={alt} width={width} height={height} loading="lazy" decoding="async" /></figure>
    <div className="solution-split-copy">{children}</div>
  </div>;
}
export function BulkRefillSolution({ onContact }) {
  return <section id="bulk-refill" className="solution-section solution-bulk" aria-labelledby="solution-bulk-title">
    <div className="solution-container">
      <ImageTextSplit image={refillJars} alt="Two branded glass jars filled with laundry detergent sheets, with kraft refill packaging" width="500" height="500">
        <SectionHeading id="solution-bulk-title" eyebrow="Bulk & Refill" title="Bulk Laundry Sheets for Refill Shops">Order loose laundry detergent sheets in bulk for refill retail, local packaging or zero-waste programs.</SectionHeading>
        <IconFeatureGrid items={bulkBenefits} className="solution-bulk-benefits" />
        <div className="solution-actions"><button type="button" className="solution-button" onClick={onContact}>Ask About Bulk Supply <FiArrowUpRight aria-hidden="true" /></button></div>
      </ImageTextSplit>
    </div>
  </section>;
}
export function ManufacturingSupport() {
  return <section id="manufacturing-support" className="solution-section solution-support" aria-labelledby="solution-support-title">
    <div className="solution-container"><SectionHeading id="solution-support-title" title="Support Beyond Manufacturing" /><IconFeatureGrid items={manufacturingSupport} className="solution-support-grid" /></div>
  </section>;
}
export function ExpandedProducts() {
  return <section className="solution-section solution-expansion" aria-labelledby="solution-expansion-title">
    <div className="solution-container">
      <div className="solution-section-topline">
        <SectionHeading id="solution-expansion-title" title="Expand Beyond Laundry Sheets">Build complementary products around your core laundry line.</SectionHeading>
        <Link className="solution-text-link" to="/products">Explore All Products <FiArrowUpRight aria-hidden="true" /></Link>
      </div>
      <ProductCategoryCards items={broaderProducts} />
    </div>
  </section>;
}
export function CTASection({ title, children, onContact }) {
  return <section className="solution-section solution-final-cta" aria-labelledby="solution-cta-title">
    <div className="solution-container solution-cta-layout">
      <div><p className="solution-eyebrow">Your Brand Starts Here</p><h2 id="solution-cta-title">{title}</h2><p>{children}</p></div>
      <div className="solution-actions">
        <button type="button" className="solution-button solution-button-light" onClick={onContact}>Start Your Project <FiArrowUpRight aria-hidden="true" /></button>
        <Link className="solution-text-link" to={LAUNDRY_COLLECTION_PATH}>Explore Laundry Sheets <FiArrowRight aria-hidden="true" /></Link>
      </div>
    </div>
  </section>;
}
export function FinalCTA({ onContact }) {
  return <CTASection title="Ready to Build Your Laundry Sheet Brand?" onContact={onContact}>Tell us what you want to make. Our team can help you identify the right product, packaging and next step.</CTASection>;
}
